import DragDropList from '../common/commonTable/DragDropList';
import { useCallback, useEffect, useState } from 'react';
import {
  IproductResponse,
  IproductResponseList,
} from '@/interfaces/productresponse.interface';
import { PageInfo } from '@/interfaces/common.interface';
import { GetFilterUrl, showToastNotification } from '@/utility/helper';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import ImagePreview from '../common/media/ImagePreview';
import Card from '../UI/Card';
import Pagination from '../common/Pagination';
import { Edit } from 'lucide-react';

interface ProductStates {
  isLoading: boolean;
  saving: boolean;
  products: IproductResponse[];
  pageInfo: PageInfo;
}

interface ProductTableProps {
  categoryId?: string;
  brandId?: string;
}

const ProductDragDrop: React.FC<ProductTableProps> = ({
  categoryId,
  brandId,
}) => {
  const [isEditing, setIsEditing] = useState<{
    isEditing: boolean;
    id: string | null;
    order: number | null | undefined;
  }>({
    isEditing: false,
    id: null,
    order: null,
  });
  const [states, setStates] = useState<ProductStates>({
    isLoading: false,
    saving: false,
    products: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
  });

  let orderKey: 'categoryOrder' | 'brandOrder';

  if (brandId) {
    orderKey = 'brandOrder';
  } else if (categoryId) {
    orderKey = 'categoryOrder';
  }

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const fetchProducts = useCallback(async () => {
    try {
      const filterQuery = {
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        status: 'active',
        brandId: brandId,
        categoryIds: categoryId,
        sort: categoryId ? 'category_order' : 'brand_order',
      };
      const query = GetFilterUrl(filterQuery);
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}?${query ? `${query}` : ''}`;
      const res = await callApi<IproductResponseList>(PATH, { method: 'GET' });

      const normalized = res.data
        .map((p, i: number) => ({
          ...p,
          [orderKey]: p[orderKey] ?? i,
        }))
        .sort((a, b) => a[orderKey] - b[orderKey]);

      handleChangeState({
        products: normalized,
        pageInfo: {
          limit: res.pageInfo.limit,
          page: res.pageInfo.page,
          total: res.pageInfo.total,
          totalPages: res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      handleChangeState({ isLoading: false });
      console.log('error is', error);
    }
  }, [states.pageInfo.page, states.pageInfo.limit]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const setProducts = (items: IproductResponse[]) => {
    handleChangeState({
      products: items.map((item, index) => ({
        ...item,
        [orderKey]: index + 1,
      })),
    });
  };

  const saveOrder = async (payload: { productId: string; order: number }[]) => {
    handleChangeState({ saving: true });
    try {
      let PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}`;
      if (brandId) {
        PATH = `${PATH}/brand/order`;
      } else if (categoryId) {
        PATH = `${PATH}/category/order`;
      }
      await callApi(PATH, {
        method: 'PATCH',
        data: { updates: payload },
      });
      fetchProducts();
      showToastNotification('success', 'Product order saved successfully');
    } catch (error) {
      ThrowError(error);
    } finally {
      handleChangeState({ saving: false });
    }
  };

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    });
  };

  const handleChangePageSize = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page: 1,
        limit,
      },
    });
  };

  const pageOptions = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
    { label: 'All', value: 20000 },
  ];

  return (
    <Card>
      <DragDropList
        title="Primary Products"
        data={states.products}
        isLoading={states.isLoading}
        getId={(item) => item.id}
        renderItem={(item) => {
          const url = item?.directImageUrl
            ? item?.directImageUrl
            : item?.media?.[0]?.url;
          return (
            <div
              key={item.id}
              className="flex items-center justify-between gap-3 w-full"
            >
              <ImagePreview
                src={`${API_URL_PRODUCT_SERVICE}/${url}`}
                text={item.name}
                textClass="w-[300px] "
              />
              <div className="w-32 flex items-center justify-between border p-1 border-dashed border-primary rounded-xl">
                <input
                  onClick={(e) => e.stopPropagation()}
                  value={
                    isEditing.id === item.id
                      ? (isEditing.order ?? item[orderKey])
                      : item[orderKey]
                  }
                  disabled={isEditing.id !== item.id}
                  onChange={(e) =>
                    setIsEditing((prev) => ({
                      ...prev,
                      order: Number(e.target.value),
                    }))
                  }
                  className="border-none outline-none w-14 text-center disabled:bg-transparent"
                />
                {isEditing.id === item.id ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      saveOrder([
                        {
                          productId: String(isEditing.id),
                          order: Number(isEditing.order),
                        },
                      ]);
                      setIsEditing({ isEditing: false, id: null, order: null });
                    }}
                    className="btn-primary py-1! text-xs px-1!"
                  >
                    Save
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      if (isEditing.id !== item.id) {
                        setIsEditing({
                          isEditing: true,
                          id: item.id,
                          order: item[orderKey],
                        });
                      }
                    }}
                  >
                    <Edit size={18} color="green" />
                  </button>
                )}
              </div>
              <span className=" text-muted">#{item[orderKey]}</span>
            </div>
          );
        }}
        onReorder={setProducts}
        onSave={() =>
          saveOrder(
            states.products.map((p) => ({
              productId: p.id,
              order: p[orderKey],
            })),
          )
        }
        saving={states.saving}
      />
      <Pagination
        pageOptions={pageOptions}
        paginationData={states.pageInfo}
        onPageSizeChange={handleChangePageSize}
        onPageChange={handleChangePage}
        isLoading={states.isLoading}
      />
    </Card>
  );
};

export default ProductDragDrop;
